/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Typography } from '@mui/material';

import { OAuth2Providers } from 'shared';

import { AuthenticationApiService } from '../../services/AuthenticationApiService';
import { ssoIcons } from '../SsoProviderButton/components/SsoProviderIcons/SsoProviderIcons';
import { ssoLinks } from '../SsoProviderButton/SsoProviderButton.consts';

export const AvailableProvidersButtons = memo(() => {
  const { t } = useTranslation('login');
  const [availableProviders, setAvailableProviders] = useState<Record<OAuth2Providers, boolean>>({
    google: false,
    microsoft: false,
    github: false,
  });
  useEffect(() => {
    const fetchAvailableProviders = async () => {
      const availableProviders = await AuthenticationApiService.checkSSOProviders();
      setAvailableProviders(availableProviders.data.payload);
    };
    void fetchAvailableProviders();
  }, []);

  const enabledProviders = useMemo(
    () => Object.entries(availableProviders).filter(([, enabled]) => enabled),
    [availableProviders],
  );

  const hasEnabledProviders = useMemo(() => enabledProviders.length > 0, [enabledProviders]);

  if (!hasEnabledProviders) {
    return null;
  }

  return (
    <>
      <Typography
        sx={{
          marginTop: theme => theme.spacing(2.25),
          marginBottom: theme => theme.spacing(0.5),
          fontWeight: 600,
          fontSize: '15px',
          letterSpacing: 0.2,
          color: theme => theme.palette.primary.dark,
          textAlign: 'center',
          width: '100%',
          display: 'block',
        }}
      >
        {t('page.login.continue_with_separator')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: 2,
          width: '100%',
        }}
      >
        {enabledProviders.map(([provider]) => (
          <Button
            key={provider}
            variant="text"
            title={provider}
            href={ssoLinks[provider as OAuth2Providers]}
            startIcon={ssoIcons[provider as OAuth2Providers]}
            sx={{
              textTransform: 'capitalize',
              fontSize: '14px',
              minWidth: 90,
              padding: theme => theme.spacing(0.5, 1.5),
              fontWeight: 500,
              letterSpacing: 0.2,
              border: 'none',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: theme => theme.palette.primary.main + '14',
              },
            }}
          >
            {provider}
          </Button>
        ))}
      </Box>
    </>
  );
});
